import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Loader2, ArrowRight, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import API from '@/lib/api';
import cookieConsent from '@/lib/cookieConsent';
import { getPostalCode, setPostalCode, isValidBelgianPostalCode, POSTAL_CODE_GATE_EVENT } from '@/lib/postalCode';

const SKIP_PATHS = ['/connexion', '/reservation', '/mot-de-passe-oublie', '/reinitialiser-mot-de-passe', '/admin', '/vendeur', '/traiteur', '/livreur', '/livraison', '/desabonnement', '/conditions-generales-de-vente', '/conditions-generales-producteurs', '/politique-confidentialite', '/politique-cookies', '/mentions-legales'];
const STAFF_ROLES = ['admin', 'seller', 'traiteur', 'driver', 'livreur'];
const isSkippedPath = (pathname) => SKIP_PATHS.some(p => pathname === p || pathname.startsWith(`${p}/`));

/** Blocking postal-code prompt shown on first arrival so the catalog is always zone-filtered. */
export default function PostalCodeGate() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [forced, setForced] = useState(false);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [zoneInfo, setZoneInfo] = useState(null);
  const [checking, setChecking] = useState(false);

  const skip = isSkippedPath(location.pathname) || (user && STAFF_ROLES.includes(user.role));

  useEffect(() => {
    if (loading) return;
    // La bannière cookies passe d'abord : la porte code postal attend un choix enregistré.
    const tryOpen = () => { if (!skip && !getPostalCode() && cookieConsent.hasMadeChoice()) { setOpen(true); setForced(true); } };
    tryOpen();
    return cookieConsent.subscribe(tryOpen);
  }, [loading, skip]);

  useEffect(() => {
    const handler = () => { setValue(getPostalCode()); setZoneInfo(null); setError(''); setForced(false); setOpen(true); };
    window.addEventListener(POSTAL_CODE_GATE_EVENT, handler);
    return () => window.removeEventListener(POSTAL_CODE_GATE_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!isValidBelgianPostalCode(value)) { setZoneInfo(null); return; }
    setChecking(true);
    API.get(`/delivery-zones/lookup?postal_code=${value}`)
      .then(r => setZoneInfo(r.data?.found ? { found: true, zone: r.data.zone } : { found: false }))
      .catch(() => setZoneInfo(null))
      .finally(() => setChecking(false));
  }, [value]);

  const submit = (e) => {
    e.preventDefault();
    if (!isValidBelgianPostalCode(value)) { setError('Entrez un code postal belge valide (4 chiffres).'); return; }
    setPostalCode(value);
    setOpen(false);
  };

  if (skip) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!forced) setOpen(o); }}>
      <DialogContent
        className="max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => forced && e.preventDefault()}
        onEscapeKeyDown={(e) => forced && e.preventDefault()}
        onInteractOutside={(e) => forced && e.preventDefault()}
        data-testid="postal-code-gate"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <MapPin className="h-5 w-5 text-brick" /> Où souhaitez-vous être livré·e ?
          </DialogTitle>
          <DialogDescription className="font-sans text-sm">
            Indiquez votre code postal pour voir uniquement les produits et plats tout prêts disponibles chez vous cette semaine.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={value}
              onChange={(e) => { setValue(e.target.value.replace(/[^0-9]/g, '')); setError(''); }}
              placeholder="Ex. 1400"
              className="h-12 text-lg font-sans tracking-widest text-center rounded-xl"
              data-testid="postal-gate-input"
            />
            <Button type="submit" className="h-12 rounded-xl bg-brick text-white hover:bg-brick/90 font-sans px-5" data-testid="postal-gate-submit">
              Voir les produits <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
          {error && <p className="text-sm text-red-600 font-sans" data-testid="postal-gate-error">{error}</p>}
          <a href="/livraison" className="inline-flex items-center gap-1 text-xs font-sans text-forest underline underline-offset-2 hover:text-forest/80" data-testid="postal-gate-map-link">
            <MapPin className="h-3 w-3" /> Voir la carte des communes livrées
          </a>

          {checking && <p className="text-xs text-muted-foreground font-sans flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Vérification de la zone…</p>}
          {!checking && zoneInfo?.found && (
            <div className="rounded-lg bg-forest/5 border-l-4 border-forest px-3 py-2 text-sm font-sans text-stone-text" data-testid="postal-gate-zone-found">
              <strong>{zoneInfo.zone.name}</strong> · livraison le {zoneInfo.zone.day_name?.toLowerCase()}
              {zoneInfo.zone.next_delivery && <> · prochaine tournée : {zoneInfo.zone.next_delivery}</>}
            </div>
          )}
          {!checking && zoneInfo && !zoneInfo.found && (
            <div className="rounded-lg bg-amber-500/5 border-l-4 border-amber-500 px-3 py-2 text-xs font-sans text-amber-900 flex gap-2" data-testid="postal-gate-zone-missing">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Pas encore de tournée régulière dans cette zone. Vous verrez les producteurs qui la desservent ponctuellement, ou le retrait chez le producteur.</span>
            </div>
          )}
          {!forced && (
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="w-full rounded-full font-sans text-muted-foreground" data-testid="postal-gate-cancel">
              Annuler
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
